import { t, Trans } from "@lingui/macro";
import { useSocialPageGroupSetQuery } from "@smp/graphql";
import { UiTypography } from "@smp/ui";
import { useFormikContext } from "formik";
import { FC, useLayoutEffect, useMemo } from "react";

import { FormSelect } from "@/components/form/form_select";
import { FormTextFieldV2 } from "@/components/form/form_text_field_v2";
import { UiGptLanguageSelect } from "@/components/ui/gpt_language_select";
import { gqlClient } from "@/hooks/use_graphql";
import { ICreateFormValues } from "@/screens/app/components/dialogs/dialog_advisor/screens/create_form";

export type IStepOccupationProps = {
  disabled?: boolean;
};

export const StepOccupation: FC<IStepOccupationProps> = ({ disabled }) => {
  const { values, setFieldValue } = useFormikContext<ICreateFormValues>();

  const { data: socialPageGroupData } = useSocialPageGroupSetQuery(gqlClient, {
    all_organization_contexts: false,
  });

  const socialPageGroupOptions = useMemo(
    () =>
      socialPageGroupData?.social_page_group_set.map((group) => ({
        label: group.name,
        value: group.id,
      })) || [],
    [socialPageGroupData?.social_page_group_set],
  );

  useLayoutEffect(() => {
    if (socialPageGroupOptions.length && !values.projectId) {
      setFieldValue("projectId", socialPageGroupOptions.at(0)?.value);
    }
  }, [setFieldValue, socialPageGroupOptions, values.projectId]);

  return (
    <>
      <UiTypography variant="h4">
        <Trans>
          What are you doing? Describe your business, product or service
        </Trans>
      </UiTypography>

      <div>
        <UiTypography variant="h5" gutterBottom>
          <Trans>
            The more details you describe, the more high-quality posts it will
            turn out
          </Trans>
        </UiTypography>

        <FormTextFieldV2
          variant="outlined"
          fullWidth
          multiline
          placeholder={t`__advisor.create_agent.form.placeholder.occupation`}
          name="middleData.occupation"
          disabled={disabled}
        />
      </div>

      {socialPageGroupOptions.length > 1 && values.projectId && (
        <div>
          <UiTypography variant="h5" gutterBottom>
            <Trans>What project will we work on?</Trans>
          </UiTypography>

          <FormSelect
            fullWidth
            options={socialPageGroupOptions}
            name="projectId"
            disabled={disabled}
          />
        </div>
      )}

      <div>
        <UiTypography variant="h5" gutterBottom>
          <Trans>Language of posts</Trans>
        </UiTypography>

        <UiGptLanguageSelect
          value={values.post.language}
          onChange={(language) => setFieldValue("post.language", language)}
          emojiSize="large"
          isDisabled={disabled}
        />
      </div>
    </>
  );
};
